import { LongPressTooltip } from '@shared/components/LongPressTooltip.js';
import { StatusIcon } from '@shared/components/StatusIcon.js';
import { useAppSelector } from '@shared/hooks/useAppSelector.js';
import { useDateTimeFormat } from '@shared/hooks/useDateTimeFormat.js';
import type { ReactElement, ReactNode } from 'react';
import { Badge } from 'react-bootstrap';
import {
  FaCheck,
  FaCloudUploadAlt,
  FaExclamationCircle,
  FaSync,
} from 'react-icons/fa';
import { BLOCKED_MESSAGE_KEYS } from '../model/actions.js';
import { useMyMapsMessages } from '../translations/useMyMapsMessages.js';

type Props = {
  mapId: string;
  /**
   * `icon` for a toolbar, where there is no room for a label and the tooltip
   * carries it; `badge` for a list, where the state reads at a glance.
   */
  as?: 'badge' | 'icon';
  className?: string;
};

// How long "Sent" stays up after a queued save lands. It reports the end of an
// offline round trip, so it is worth seeing for a while and worth forgetting
// afterwards — a map that says "Sent" a month on says nothing at all, while
// every map that never went offline says nothing to begin with.
const SENT_SHOWN_FOR = 24 * 3600_000;

/**
 * Where a map's saved changes stand with the server: waiting to be sent, being
 * sent, refused, or through. Shown wherever a map is named, so the map list and
 * the active-map toolbar can't drift apart on what they report.
 *
 * Renders nothing for a map that has never had a save queued — the ordinary
 * case, where the server is simply always up to date.
 */
export function MapSyncStatus({
  mapId,
  as = 'badge',
  className,
}: Props): ReactElement | null {
  const mm = useMyMapsMessages();

  const entry = useAppSelector((state) =>
    state.myMaps.outbox.find((entry) => entry.mapId === mapId),
  );

  const syncing = useAppSelector((state) =>
    state.myMaps.syncingIds.includes(mapId),
  );

  const lastSyncedAt = useAppSelector((state) => {
    const at = state.myMaps.lastSyncedAt[mapId];

    return at !== undefined && Date.now() - at < SENT_SHOWN_FOR
      ? at
      : undefined;
  });

  const dateFormat = useDateTimeFormat({
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  // One description of the state, worn either way: the icon and the colour are
  // the badge's own, and the label is its text or, in a toolbar, what a screen
  // reader reads in place of the icon.
  const state: {
    icon: ReactNode;
    color: string;
    label: string | undefined;
    tooltip: ReactNode;
  } | null = entry?.blocked
    ? {
        // Not the triangle: that one is the unsaved-changes marker, and both can
        // be up at once.
        icon: <FaExclamationCircle />,
        color: 'danger',
        label:
          entry.blocked === 'conflict'
            ? mm?.outboxConflictBadge
            : mm?.outboxBlockedBadge,
        tooltip: mm?.[BLOCKED_MESSAGE_KEYS[entry.blocked]]({
          name: entry.name,
        }),
      }
    : entry && syncing
      ? {
          icon: <FaSync />,
          color: 'info',
          label: mm?.syncing,
          tooltip: mm?.syncing,
        }
      : entry
        ? {
            icon: <FaCloudUploadAlt />,
            color: 'warning',
            label: mm?.unsent,
            tooltip: mm?.unsentTooltip,
          }
        : lastSyncedAt === undefined
          ? null
          : {
              icon: <FaCheck />,
              color: 'success',
              label: mm?.synced,
              tooltip: mm?.syncedAt({ at: dateFormat.format(lastSyncedAt) }),
            };

  if (!state) {
    return null;
  }

  if (as === 'icon') {
    return (
      <StatusIcon
        icon={state.icon}
        color={state.color}
        label={state.label}
        tooltip={state.tooltip}
        className={className}
      />
    );
  }

  return (
    <LongPressTooltip label={state.tooltip}>
      {({ props }) => (
        <span className={className} {...props}>
          <Badge
            bg={state.color}
            text={state.color === 'danger' ? undefined : 'dark'}
          >
            {state.icon} {state.label}
          </Badge>
        </span>
      )}
    </LongPressTooltip>
  );
}
